import React, { useEffect, useMemo, useState } from "react";

import { HelpIcon as Help, Label, Typography } from "@packages/ds";
import moment from "moment";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "react-router-dom";

import EmptyText from "Components/EmptyText";
import ModalConfirmLeaveForm from "Components/ModalConfirmLeaveForm";
import PageMeta from "Components/PageMeta";
import { APP_NAME } from "Constants/documentationUrls";
import Button, { ButtonWrapper, Link } from "ds/Button";
import Modal from "ds/Modal";
import Loading from "ds/PageLoading";
import SettingLine from "ds/SettingLine";
import * as Table from "ds/Table";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { capitalize } from "Libs/utils";
import { gitProjectSelector } from "Reducers/project/project";
import {
  loadCertificates,
  deleteCertificate,
  certificateSelector
} from "Reducers/project/settings/certificate";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import {
  InfoLayout,
  CertificateExpanded,
  CertificateWrapper,
  FieldGroup,
  DeleteButton,
  NoteWrapper,
  AddButton,
  Title,
  PageDescription,
  TableThead,
  TableTh
} from "./ProjectCertificateListField.styles";

import type { Certificate } from "@packages/client";

const ProjectCertificateListField = () => {
  const { organizationId, projectId } = useParams<{
    organizationId: string;
    projectId: string;
  }>();

  const intl = useIntl();
  const dispatch = useAppDispatch();

  const project = useSelectorWithUrlParams(gitProjectSelector);
  const projectCertificate = useAppSelector(state =>
    certificateSelector(state)
  );

  const projectCertificateIsLoading = projectCertificate.loading || !project;

  const certificates = useMemo(
    () =>
      projectCertificate.all.filter(
        (certificate: unknown): certificate is Certificate =>
          JSON.stringify(certificate) !== "{}"
      ) || projectCertificate.all,
    [projectCertificate.all]
  );

  const [isChanged, setIsChanged] = useState(false);
  const [changeIndex, setChangeIndex] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [expandedLine, setExpandedLine] = useState<number | null>(null);
  const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);
  const [isModalLeaveOpen, setIsModalLeaveOpen] = useState(false);
  const [certificateToDelete, setCertificateToDelete] = useState<Certificate>();
  const [sort, setSort] = Table.useSort<{
    certificate: string;
    expiryDate: string;
  }>({
    key: "certificate",
    direction: "asc"
  });

  useEffect(() => {
    if (project?.id && organizationId) {
      dispatch(
        loadCertificates({ organizationDescriptionId: organizationId, project })
      );
    }
  }, [dispatch, organizationId, project]);

  const expand = (index: number | null) => {
    if (isChanged && !isModalLeaveOpen) {
      setIsModalLeaveOpen(true);
      setChangeIndex(true);
      setExpandedIndex(index);
    } else if (expandedLine === index) {
      setExpandedLine(null);
    } else {
      setExpandedLine(index);
      setIsChanged(false);
    }
  };

  const openDeleteModal = (certificate: Certificate) => {
    setIsModalDeleteOpen(true);
    setCertificateToDelete(certificate);
  };

  const closeModals = () => {
    setIsModalDeleteOpen(false);
    setIsModalLeaveOpen(false);
  };

  const sortedCertificate = Object.values(certificates).sort((a, b) => {
    if (sort.key === "certificate") {
      const aName = a.domains?.join(", ");
      const bName = b.domains?.join(", ");
      if (aName < bName) {
        return sort.direction === "asc" ? -1 : 1;
      }

      if (aName > bName) {
        return sort.direction === "asc" ? 1 : -1;
      }
      return 0;
    }

    if (a.expires_at < b.expires_at) {
      return sort.direction === "asc" ? -1 : 1;
    }

    if (a.expires_at > b.expires_at) {
      return sort.direction === "asc" ? 1 : -1;
    }
    return 0;
  });

  return (
    <CertificateWrapper>
      <PageMeta title={`Certificates | ${project?.title}`} />
      <AddButton id="add-new-certificate" analyticId="certificates" to="add" />
      <Title id="settings-heading" tag="h2">
        <FormattedMessage id="certificates" />
      </Title>
      <PageDescription>
        {intl.formatMessage(
          { id: "certificates.description" },
          { appName: APP_NAME }
        )}
        <br />
        {intl.formatMessage({ id: "certificates.description2" })}
      </PageDescription>
      <NoteWrapper>
        <Help />
        <span>
          {intl.formatMessage({ id: "certificates.info" })}{" "}
          <Link
            href={intl.formatMessage({
              id: "links.documentation.configure_project.certificates"
            })}
          >
            {intl.formatMessage({ id: "certificates.documentation" })}
          </Link>
        </span>
      </NoteWrapper>
      <div aria-labelledby="settings-heading">
        {!projectCertificateIsLoading && certificates.length === 0 && (
          <EmptyText>
            {intl.formatMessage({ id: "certificates.no_certificates" })}
          </EmptyText>
        )}
        {projectCertificateIsLoading ? (
          <Loading />
        ) : (
          <Table.Table>
            <TableThead>
              <TableTh
                first
                onSortChanged={() => setSort("certificate")}
                isSortActive={sort.key === "certificate"}
                sortDirection={sort.direction}
              >
                <Typography.BodyText weight="semiBold" size="extraSmall">
                  {intl.formatMessage({ id: "certificates.sort.name" })}
                </Typography.BodyText>
              </TableTh>
              <TableTh
                onSortChanged={() => setSort("expiryDate")}
                isSortActive={sort.key === "expiryDate"}
                sortDirection={sort.direction}
              >
                <Typography.BodyText weight="semiBold" size="extraSmall">
                  {intl.formatMessage({ id: "certificates.sort.expiryDate" })}
                </Typography.BodyText>
              </TableTh>
            </TableThead>
          </Table.Table>
        )}
        {sortedCertificate.map((certificate, index) => {
          return (
            <div key={`${certificate.id}-${index}-read`}>
              <SettingLine
                id={`project-certificate-list-${(
                  certificate.domains?.[0] || ""
                ).replace(/\./g, "")}`}
                info={
                  <InfoLayout>
                    <span
                      className="domains"
                      title={certificate.domains?.join(", ")}
                    >
                      {certificate.domains?.join(", ")}
                    </span>
                    <span
                      className="expires"
                      title={moment(certificate.expires_at).format(
                        "MMMM D, YYYY"
                      )}
                    >
                      Exp:{" "}
                      {moment(certificate.expires_at).format("MMMM D, YYYY")}
                    </span>
                  </InfoLayout>
                }
                isNew={!certificate.id}
                addNewTitle="Add certificate"
                isOpen={expandedLine === index}
                openText={intl.formatMessage({ id: "view" })}
                onClick={() => expand(index)}
              >
                {expandedLine === index && (
                  <CertificateExpanded key={`${certificate.id}-${index}-edit`}>
                    <FieldGroup>
                      <Label>
                        <FormattedMessage id="domains" />
                      </Label>
                      <div>
                        {certificate.domains?.map(domain => (
                          <div className="domain" key={domain}>
                            {domain}
                          </div>
                        ))}
                      </div>
                    </FieldGroup>
                    <FieldGroup>
                      <Label>
                        <FormattedMessage id="issuer_info" />
                      </Label>
                      <div>
                        {(
                          certificate.issuer as unknown as {
                            oid: string;
                            alias: string;
                            value: string;
                          }[]
                        )?.map(issuer => (
                          <div className="issuer" key={issuer.oid}>
                            {issuer.value}
                          </div>
                        ))}
                      </div>
                    </FieldGroup>
                    <FieldGroup className="inline">
                      <Label>
                        <FormattedMessage id="created" />
                      </Label>
                      <div>
                        {moment(certificate.created_at).format("MMMM D YYYY")}
                      </div>
                    </FieldGroup>
                    <FieldGroup className="inline">
                      <Label>
                        <FormattedMessage id="updated" />
                      </Label>
                      <div>
                        {moment(certificate.updated_at).format("MMMM D YYYY")}
                      </div>
                    </FieldGroup>
                    <FieldGroup className="inline">
                      <Label>
                        <FormattedMessage id="expires" />
                      </Label>
                      <div>
                        {moment(certificate.expires_at).format("MMMM D YYYY")}
                      </div>
                    </FieldGroup>
                    <div className="actions">
                      {certificate.hasLink?.("#delete") && (
                        <DeleteButton
                          analyticId="delete"
                          variant="outline"
                          id={`delete-certificate-${(
                            certificate.domains?.[0] || ""
                          ).replace(/\./g, "")}`}
                          type="button"
                          aria-label={intl.formatMessage({
                            id: "delete"
                          })}
                          onClick={() => openDeleteModal(certificate)}
                        >
                          {capitalize(intl.formatMessage({ id: "delete" }))}
                        </DeleteButton>
                      )}
                    </div>
                  </CertificateExpanded>
                )}
              </SettingLine>
            </div>
          );
        })}
        <Modal
          isOpen={isModalDeleteOpen}
          onOpenChange={closeModals}
          id={certificateToDelete?.id}
        >
          <Typography.Title tag="h2">
            {intl.formatMessage({ id: "settings.variables.modal.title" })}
          </Typography.Title>
          <Typography.BodyText>
            {intl.formatMessage({
              id: "settings.certificates.modal.body"
            })}
            {intl.formatMessage({
              id: "settings.certificates.modal.delete"
            })}
            <strong>{" " + certificateToDelete?.domains.join(", ")}</strong>
          </Typography.BodyText>
          <ButtonWrapper spacing="modal" justifyContent="end">
            <Button
              variant="secondary"
              analyticId="cancel"
              onClick={closeModals}
            >
              {intl.formatMessage({ id: "cancel" })}
            </Button>
            <Button
              id={`delete-${certificateToDelete?.id}-confirm-modal-button`}
              onClick={() => {
                if (organizationId && projectId) {
                  dispatch(
                    deleteCertificate({
                      organizationDescriptionId: organizationId,
                      projectDescriptionId: projectId,
                      certificate: certificateToDelete
                    })
                  );
                }
                closeModals();
                setExpandedLine(null);
              }}
            >
              {capitalize(intl.formatMessage({ id: "delete" }))}
            </Button>
          </ButtonWrapper>
        </Modal>

        <ModalConfirmLeaveForm
          isOpen={isModalLeaveOpen}
          closeModal={closeModals}
          continueFunction={() => {
            changeIndex && expand(expandedIndex);
          }}
          cancelFunction={closeModals}
        />
      </div>
    </CertificateWrapper>
  );
};

export default ProjectCertificateListField;
